import React from "react";
import { Button, Modal } from "components/ui/BootstrapCompat";
import { useQuery, useQueryClient } from "react-query";
import useLocalStorage from "helpers/useLocalStorage";
import { errorAdd, successAdd } from "helpers/sweetalert";
import { userWalletGetMe } from "services/user.service";
import { donateWithdrawCreateOne } from "services/withdraw.service";
import { WITHDRAW_FEE_AMOUNT } from "../withdraw.types";
import { formatCurrency } from "../withdraw.utils";

type Props = {
  show: boolean;
  onClose: () => void;
};

export default function WithdrawConfirmModal({ show, onClose }: Props) {
  const [token] = useLocalStorage("token", "");
  const [businessId] = useLocalStorage("businessId", "");
  const queryClient = useQueryClient();

  const { data: walletData } = useQuery(
    ["wallet-me", businessId],
    async () => {
      if (!token) return null;
      return await userWalletGetMe({ token, businessId });
    },
    { enabled: !!token },
  );

  const walletPayload = (walletData as any)?.data ?? walletData;
  const availableAmount = Number(
    walletPayload?.amount ?? walletPayload?.balance ?? 0,
  );
  const currencyLabel = String(walletPayload?.currency || "ກີບ");
  const feeAmount = availableAmount > 0 ? WITHDRAW_FEE_AMOUNT : 0;
  const netAmount = Math.max(availableAmount - feeAmount, 0);

  const [note, setNote] = React.useState("");
  const [isConfirming, setIsConfirming] = React.useState(false);

  React.useEffect(() => {
    if (show) {
      setNote("");
    }
  }, [show]);

  const confirmWithdraw = async () => {
    setIsConfirming(true);
    try {
      await donateWithdrawCreateOne({
        body: {
          amount: netAmount,
          message: note?.trim() ? note.trim() : undefined,
        },
        token,
        businessId,
      });

      successAdd("ສົ່ງຄຳຂໍຖອນເງິນແລ້ວ");
      onClose();
    } catch (error: any) {
      errorAdd(error?.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    } finally {
      queryClient.invalidateQueries(["withdraw-get-many"]);
      queryClient.invalidateQueries(["wallet-me", businessId]);
      setIsConfirming(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton={!isConfirming}>
        <Modal.Title>ຢືນຢັນການຖອນເງິນ</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="grid gap-3">
          <div className="rounded border border-gray-200 bg-gray-50 p-3">
            <div className="text-sm text-gray-500">ຍອດຫັກຈາກກະເປົາ</div>
            <div className="text-2xl font-bold text-primary">
              {formatCurrency(availableAmount)} {currencyLabel}
            </div>
          </div>

          <div className="rounded border border-gray-200 bg-white p-3">
            <div className="flex items-center justify-between gap-3 text-sm text-gray-500">
              <span>
                ຄ່າທຳນຽມ ({formatCurrency(feeAmount)} {currencyLabel})
              </span>
              <strong>
                {formatCurrency(feeAmount)} {currencyLabel}
              </strong>
            </div>
            <div className="mt-2 flex items-center justify-between gap-3 text-sm text-gray-500">
              <span>ຍອດທີ່ຈະໄດ້ຮັບ</span>
              <strong>
                {formatCurrency(netAmount)} {currencyLabel}
              </strong>
            </div>
          </div>

          <div className="rounded border border-gray-200 bg-white p-3">
            <label className="mb-1 block text-sm text-gray-500">
              ໝາຍເຫດ (ບໍ່ບັງຄັບ)
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="ລະບຸໝາຍເຫດສຳລັບຄຳຂໍນີ້"
              disabled={isConfirming}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isConfirming}>
          ຍົກເລີກ
        </Button>
        <Button
          variant="primary"
          onClick={confirmWithdraw}
          disabled={isConfirming || !token || netAmount <= 0}
        >
          {isConfirming ? "ກຳລັງສົ່ງ..." : "ຢືນຢັນຖອນເງິນ"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
